import React, { useState, useEffect, useCallback } from 'react';
import { projectTemplatesAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Copy, Trash2, Loader2, Home, Layers3, ListChecks, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

export default function ProjectTemplates() {
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptySave = { project_id: '', name: '', description: '' };
  const [saveForm, setSaveForm] = useState(emptySave);

  const emptyClone = { name: '', city: '', description: '', client_id: '' };
  const [cloneForm, setCloneForm] = useState(emptyClone);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [t, p] = await Promise.all([projectTemplatesAPI.list(), projectsAPI.list()]);
      setTemplates(t.data); setProjects(p.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!saveForm.project_id) { toast.error('Select a project'); return; }
    setFormLoading(true);
    try {
      await projectTemplatesAPI.saveFromProject(saveForm.project_id, { name: saveForm.name, description: saveForm.description });
      toast.success('Template saved');
      setSaveOpen(false); setSaveForm(emptySave); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleClone = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!cloneForm.name.trim()) { toast.error('Project name required'); return; }
    setFormLoading(true);
    try {
      const res = await projectTemplatesAPI.clone(selected.template_id, cloneForm);
      toast.success(`Project created with ${res.data.cloned.rooms} rooms, ${res.data.cloned.phases} phases, ${res.data.cloned.tasks} tasks`);
      setCloneOpen(false); setCloneForm(emptyClone); setSelected(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await projectTemplatesAPI.delete(t.template_id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="project-templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
              <Layers3 className="h-7 w-7 text-sky-500" />Project Templates
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Save your favourite project structures and clone them for new jobs</p>
          </div>
          <Button onClick={() => setSaveOpen(true)} data-testid="save-template-btn"><Plus className="h-4 w-4 mr-2" />Save From Project</Button>
        </div>

        {loading ? <div className="animate-pulse space-y-3">{[1,2].map(i => <div key={i} className="h-24 bg-muted/40 rounded-xl" />)}</div> :
          templates.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><Layers3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No templates yet. Save an existing project's structure to reuse it.</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.template_id} className="hover:shadow-md transition-all" data-testid={`template-${t.template_id}`}>
                  <CardContent className="p-5">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-muted-foreground">From: {t.source_project}</div>
                    {t.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{t.description}</p>}
                    <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                      <div className="flex flex-col items-center p-2 bg-sky-50 rounded"><Home className="h-3 w-3 mb-1" />{t.rooms?.length || 0} rooms</div>
                      <div className="flex flex-col items-center p-2 bg-amber-50 rounded"><CalendarClock className="h-3 w-3 mb-1" />{t.phases?.length || 0} phases</div>
                      <div className="flex flex-col items-center p-2 bg-violet-50 rounded"><ListChecks className="h-3 w-3 mb-1" />{t.tasks?.length || 0} tasks</div>
                      <div className="flex flex-col items-center p-2 bg-emerald-50 rounded"><ListChecks className="h-3 w-3 mb-1" />{t.checklists?.length || 0} lists</div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button size="sm" onClick={() => { setSelected(t); setCloneForm({ ...emptyClone, name: `Project from ${t.name}` }); setCloneOpen(true); }} data-testid={`clone-${t.template_id}`}><Copy className="h-3 w-3 mr-1" />New Project from Template</Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(t)} className="text-red-600 ml-auto"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        }
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save Project as Template</DialogTitle><DialogDescription>Captures rooms, phases, tasks, and checklists (not data like budgets or invoices)</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div><Label>Source Project *</Label>
              <Select value={saveForm.project_id} onValueChange={v => setSaveForm({...saveForm, project_id: v})}>
                <SelectTrigger data-testid="template-source-select"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Template Name</Label><Input placeholder="e.g. 3BHK Luxury Template" value={saveForm.name} onChange={e => setSaveForm({...saveForm, name: e.target.value})} data-testid="template-name-input" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={saveForm.description} onChange={e => setSaveForm({...saveForm, description: e.target.value})} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="confirm-save-template-btn">{formLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Template</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Project from "{selected?.name}"</DialogTitle></DialogHeader>
          <form onSubmit={handleClone} className="space-y-3">
            <div><Label>Project Name *</Label><Input value={cloneForm.name} onChange={e => setCloneForm({...cloneForm, name: e.target.value})} required data-testid="clone-name-input" /></div>
            <div><Label>City</Label><Input value={cloneForm.city} onChange={e => setCloneForm({...cloneForm, city: e.target.value})} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={cloneForm.description} onChange={e => setCloneForm({...cloneForm, description: e.target.value})} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloneOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="confirm-clone-btn">{formLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
